<script lang="ts" setup>
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import useCollectSubmit from '@Use/useCollectSubmit';
import CollectAccountInfo from '@Components/CollectAccountInfo.vue';
import PlaidConnectModal from '@Components/modals/PlaidConnectModal.vue';
import { FlowLayout } from '@Shared';

// Setup
const companyImageBaseUrl = useCompanyImageBaseUrl();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const { goBackInFlow } = useFlowNavigation();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();

// Submit + optional Plaid upgrade (PLAT-2857) — action switch lives in the composable.
const {
    isSubmitting,
    errorMessage,
    plaidLinkToken,
    isPlaidModalOpen,
    isCompletingPlaid,
    submit,
    onPlaidSuccess,
    onPlaidExit,
} = useCollectSubmit();

// Actions
const onSubmit = async () => {
    const wi = requestSessionStore.collectWiringInstructions;
    await submit({
        bankName: wi.beneficiaryBank?.name ?? wi.bankName ?? '',
        accountNumber: wi.creditTo?.accountNumber ?? wi.accountNumber ?? '',
        routingNumber: wi.beneficiaryBank?.routingNumber ?? wi.routingNumber ?? '',
        accountType: wi.bankAccountInfo?.accountType ?? wi.accountType ?? '',
        accountHolderName: wi.creditTo?.accountName ?? wi.accountHolderName ?? '',
        isJoint: wi.bankAccountInfo?.isJoint ?? false,
        accountHolderType: wi.creditTo?.accountHolderType ?? null,
        accountHolderFirstName: wi.creditTo?.accountHolderFirstName ?? null,
        accountHolderLastName: wi.creditTo?.accountHolderLastName ?? null,
        accountHolderBusinessName: wi.creditTo?.accountHolderBusinessName ?? null,
        personalAddress: {
            address1: wi.personalAddress?.address1 ?? '',
            address2: wi.personalAddress?.address2 ?? '',
            city: wi.personalAddress?.city ?? '',
            state: wi.personalAddress?.state ?? '',
            postalCode: wi.personalAddress?.postalCode ?? '',
        },
        furtherCreditToAccountName: wi.furtherCreditTo?.accountName ?? '',
        furtherCreditToAccountNumber: wi.furtherCreditTo?.accountNumber ?? '',
    });
};
</script>

<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <!-- Full-page loader while the Plaid completion call records the attempt; without it
             the review screen re-appears for a few seconds after the Plaid overlay closes. -->
        <app-page-loader v-if="isCompletingPlaid" />

        <app-form-content-layout
            v-else
            class="collect-review__content"
        >
            <h1 class="collect-review__headline">Review account details</h1>

            <p class="collect-review__description">
                The funds will be deposited to the bank account below. Please double-check these details before submitting.
            </p>

            <p
                v-if="errorMessage"
                class="collect-review__error"
                role="alert"
            >
                {{ errorMessage }}
            </p>

            <collect-account-info
                :wiring-instructions="requestSessionStore.collectWiringInstructions"
                toggle-data-id="collect-review-account-details-toggle"
                collapse-initial-state="opened"
            />
        </app-form-content-layout>

        <div
            v-if="!isCompletingPlaid"
            class="collect-review__buttons"
        >
            <app-button
                class="collect-review__button"
                expanded
                size="large"
                :is-loading="isSubmitting"
                data-id="collect-review-submit"
                @onClick="onSubmit"
            >
                Submit
            </app-button>
        </div>

        <plaid-connect-modal
            v-if="isPlaidModalOpen && plaidLinkToken"
            v-model="isPlaidModalOpen"
            :link-token="plaidLinkToken"
            @on-success="onPlaidSuccess"
            @on-exit="onPlaidExit"
        />
    </flow-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.collect-review {
    &__content {
        margin-bottom: 2rem;
    }

    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        margin: 0;
    }

    &__buttons {
        display: flex;
        flex-direction: column;
    }
}
</style>
